import { useMemo } from "react";
import { useSearchPageParams, useI18N } from "common/hooks";

/**
 * Custom hook to derive the display text for search terms across the app.
 * Handles different search types (nearby, brand, venue_name, etc.) and returns
 * the appropriate display text for the current search state.
 *
 * @returns The derived search term to display, or empty string if none
 */
export function useSearchDisplayTerm(): string {
  const { searchPageParams } = useSearchPageParams();
  const i18n = useI18N();

  return useMemo(() => {
    // A focused venue is searched by place id with an empty text term, so its
    // name lives in focus_search — show that rather than falling back to "Nearby".
    if (
      !searchPageParams.search &&
      searchPageParams.focus_place_ids &&
      searchPageParams.focus_search
    ) {
      return searchPageParams.focus_search;
    }
    // For nearby searches, show short address (simpleLocationText) not the long API address
    if (searchPageParams.search_type === "nearby") {
      return (
        searchPageParams.simpleLocationText || i18n.get("nearBy") || "Nearby"
      );
    }
    // For brand and venue_name, show the display text (not location or ID)
    if (
      searchPageParams.search_type === "brand" ||
      searchPageParams.search_type === "venue_name"
    ) {
      return searchPageParams.search || searchPageParams.search_value;
    }
    // For other search types with search_value
    if (searchPageParams.search_type && searchPageParams.search_value) {
      return searchPageParams.search_value;
    }
    // If there's a general search term
    if (searchPageParams.search) {
      return searchPageParams.search;
    }
    return "";
  }, [
    searchPageParams.search_type,
    searchPageParams.search_value,
    searchPageParams.search,
    searchPageParams.simpleLocationText,
    searchPageParams.focus_place_ids,
    searchPageParams.focus_search,
    i18n,
  ]);
}
